import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, BookOpen } from "lucide-react";

const HERO_IMG =
  "https://images.unsplash.com/photo-1591622180684-b96c52ef3908?crop=entropy&cs=srgb&fm=jpg&q=85&ixlib=rb-4.1.0&w=2200";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.06, duration: 0.55, ease: [0.22, 1, 0.36, 1] },
  }),
};

const SCROLL_HINT = {
  initial: { opacity: 0 },
  animate: { opacity: 1 },
  transition: { delay: 0.6, duration: 1.2 },
};

const BG_STYLE = {
  backgroundImage: `url(${HERO_IMG})`,
  backgroundSize: "cover",
  backgroundPosition: "center 40%",
};

const BG_FADE_STYLE = {
  background:
    "linear-gradient(180deg, hsl(var(--background)) 0%, hsl(var(--background) / 0.55) 35%, hsl(var(--background) / 0.85) 80%, hsl(var(--background)) 100%)",
};

function HeroBackground() {
  return (
    <>
      <div className="absolute inset-0 -z-10 duotone-strong" style={BG_STYLE} aria-hidden />
      <div className="absolute inset-0 -z-10" style={BG_FADE_STYLE} aria-hidden />
    </>
  );
}

function HeroKicker() {
  return (
    <motion.div
      initial="hidden"
      animate="show"
      variants={fadeUp}
      className="flex items-center gap-3 mb-8"
    >
      <span className="pulse-dot" aria-hidden />
      <span className="eyebrow">NewWaysToday · Field Dispatch № 100</span>
    </motion.div>
  );
}

function HeroHeadline() {
  return (
    <motion.h1
      initial="hidden"
      animate="show"
      custom={1}
      variants={fadeUp}
      className="hero-title max-w-[14ch]"
      data-testid="hero-title"
    >
      The<br />Hundredth<br />Monkey<span className="text-[hsl(var(--primary))]">.</span>
    </motion.h1>
  );
}

function HeroLede() {
  return (
    <>
      <motion.p
        initial="hidden"
        animate="show"
        custom={2}
        variants={fadeUp}
        className="font-serif-display italic mt-10 max-w-2xl text-2xl md:text-3xl leading-[1.15]"
      >
        "There is no cure for nuclear war —{" "}
        <span className="not-italic font-bold text-[hsl(var(--primary))]">
          only prevention.
        </span>
        "
      </motion.p>
      <motion.p
        initial="hidden"
        animate="show"
        custom={3}
        variants={fadeUp}
        className="font-body mt-6 max-w-xl text-lg md:text-xl leading-relaxed opacity-90"
      >
        Ken Keyes Jr.'s 1981 anti-nuclear classic — restored as a free,
        sharable digital edition. One more reader could be the one that
        tips the field.
      </motion.p>
    </>
  );
}

function HeroCtas() {
  return (
    <motion.div
      initial="hidden"
      animate="show"
      custom={4}
      variants={fadeUp}
      className="mt-10 flex flex-wrap items-center gap-4"
    >
      <Link to="/read" className="btn-brutal" data-testid="hero-cta-read">
        <BookOpen size={18} />
        Read The Book
        <ArrowRight size={18} />
      </Link>
      <a href="#signup" className="btn-ghost" data-testid="hero-cta-join">
        Join NewWaysToday
      </a>
    </motion.div>
  );
}

export default function HomeHero() {
  return (
    <section
      className="relative border-b-2 border-current overflow-hidden"
      data-testid="hero-section"
    >
      <HeroBackground />
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-20 pb-28 md:pt-32 md:pb-40 relative">
        <HeroKicker />
        <HeroHeadline />
        <HeroLede />
        <HeroCtas />
        <motion.div
          {...SCROLL_HINT}
          className="absolute right-5 md:right-10 bottom-8 hidden md:flex items-center gap-3"
        >
          <span className="vertical-text">Scroll · 1952 · Koshima Island</span>
        </motion.div>
      </div>
    </section>
  );
}
